import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface CalendarProps {
  appointments: any[];
  onDayClick?: (day: Date, appointments: any[]) => void;
  onAppointmentClick?: (appointment: any) => void;
}

const DAYS_OF_WEEK = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const MONTHS_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const TYPE_LABELS: Record<string, string> = {
  checkup: 'Kontrol',
  vaccination: 'Aşı',
  surgery: 'Ameliyat',
  emergency: 'Acil',
  examination: 'Muayene',
  other: 'Diğer',
};

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  checkup:     { bg: 'bg-[#557236]',  text: 'text-white' },
  vaccination: { bg: 'bg-purple-500', text: 'text-white' },
  surgery:     { bg: 'bg-rose-500',   text: 'text-white' },
  emergency:   { bg: 'bg-amber-500',  text: 'text-white' },
  examination: { bg: 'bg-blue-500',   text: 'text-white' },
  other:       { bg: 'bg-[#8A8676]',  text: 'text-white' },
};

const AppointmentCalendar: React.FC<CalendarProps> = ({
  appointments,
  onAppointmentClick,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const { calendarDays, monthLabel, year } = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstWeekday = (firstDay.getDay() + 6) % 7;
    const daysInMonth = lastDay.getDate();
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    const days: { date: Date; isCurrentMonth: boolean; isToday: boolean }[] = [];

    for (let i = firstWeekday - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonthLastDay - i), isCurrentMonth: false, isToday: false });
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month, i);
      days.push({ date: d, isCurrentMonth: true, isToday: d.getTime() === today.getTime() });
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false, isToday: false });
    }

    return { calendarDays: days, monthLabel: MONTHS_TR[month], year };
  }, [currentDate]);

  const getAppointmentsForDay = (date: Date) =>
    appointments.filter(a => {
      if (!a.date) return false;
      const d = new Date(a.date);
      return d.getFullYear() === date.getFullYear() &&
             d.getMonth() === date.getMonth() &&
             d.getDate() === date.getDate();
    });

  const goToPrev = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const goToNext = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToToday = () => { setCurrentDate(new Date()); setSelectedDay(null); };

  const selectedDayAppts = selectedDay ? getAppointmentsForDay(selectedDay) : [];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2">
          <button onClick={goToPrev}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-bg-soft)'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = ''}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h3 className="text-[15px] font-bold min-w-[140px] text-center" style={{ color: 'var(--color-text-strong)' }}>
            {monthLabel} {year}
          </h3>
          <button onClick={goToNext}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-bg-soft)'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = ''}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <button onClick={goToToday}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors"
          style={{ backgroundColor: 'rgba(61,139,125,0.1)', color: '#557236', border: '1px solid rgba(61,139,125,0.2)' }}
        >
          <CalendarIcon className="w-3.5 h-3.5" />
          Bugün
        </button>
      </div>

      {/* Gün başlıkları */}
      <div className="grid grid-cols-7" style={{ backgroundColor: 'var(--color-bg-soft)', borderBottom: '1px solid var(--color-border)' }}>
        {DAYS_OF_WEEK.map(d => (
          <div key={d} className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--color-text-subtle)' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Takvim ızgarası */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, idx) => {
          const dayAppts = getAppointmentsForDay(day.date);
          const isSelected = selectedDay && day.date.getTime() === selectedDay.getTime();

          return (
            <button
              key={idx}
              onClick={() => setSelectedDay(day.date)}
              className="min-h-[76px] p-2 text-left transition-all relative"
              style={{
                borderBottom: '1px solid var(--color-border-soft)',
                borderRight: '1px solid var(--color-border-soft)',
                backgroundColor: isSelected
                  ? 'rgba(61,139,125,0.08)'
                  : !day.isCurrentMonth
                  ? 'var(--color-bg-soft)'
                  : 'transparent',
                outline: isSelected ? '2px solid rgba(61,139,125,0.4)' : 'none',
                outlineOffset: '-2px',
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-[13px] font-semibold ${
                    day.isToday
                      ? 'w-6 h-6 rounded-full bg-[#557236] text-white flex items-center justify-center text-[11px]'
                      : ''
                  }`}
                  style={
                    !day.isToday
                      ? { color: day.isCurrentMonth ? 'var(--color-text-strong)' : 'var(--color-text-subtle)' }
                      : {}
                  }
                >
                  {day.date.getDate()}
                </span>
                {dayAppts.length > 0 && (
                  <span className="text-[9px] font-bold text-[#557236]">{dayAppts.length}</span>
                )}
              </div>

              <div className="space-y-0.5">
                {dayAppts.slice(0, 2).map(appt => {
                  const col = TYPE_COLORS[appt.type] || TYPE_COLORS.other;
                  return (
                    <div
                      key={appt.id}
                      onClick={e => { e.stopPropagation(); onAppointmentClick?.(appt); }}
                      className={`text-[9px] truncate px-1 py-0.5 rounded ${col.bg} ${col.text} font-medium cursor-pointer`}
                      title={`${appt.time || ''} ${appt.pet?.name || ''}`}
                    >
                      {appt.time && <span className="opacity-80">{appt.time.slice(0, 5)} </span>}
                      {appt.pet?.name}
                    </div>
                  );
                })}
                {dayAppts.length > 2 && (
                  <div className="text-[9px] font-semibold px-1" style={{ color: 'var(--color-text-muted)' }}>
                    +{dayAppts.length - 2} daha
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Seçili gün detayları */}
      <AnimatePresence>
        {selectedDay && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
            style={{ borderTop: '1px solid var(--color-border)' }}
          >
            <div className="p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-3" style={{ color: 'var(--color-text-muted)' }}>
                {selectedDay.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              {selectedDayAppts.length === 0 ? (
                <p className="text-[13px] text-center py-4" style={{ color: 'var(--color-text-subtle)' }}>Bu gün randevu yok</p>
              ) : (
                <div className="space-y-2">
                  {selectedDayAppts
                    .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                    .map(appt => {
                      const col = TYPE_COLORS[appt.type] || TYPE_COLORS.other;
                      return (
                        <button
                          key={appt.id}
                          onClick={() => onAppointmentClick?.(appt)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left"
                          style={{ backgroundColor: 'var(--color-bg-soft)', border: '1px solid var(--color-border-soft)' }}
                          onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(61,139,125,0.07)'}
                          onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-bg-soft)'}
                        >
                          <div className={`w-1.5 h-10 rounded-full shrink-0 ${col.bg}`} />
                          <div className="w-10 text-center shrink-0">
                            <p className="text-[11px] font-bold" style={{ color: '#557236' }}>{appt.time || '--:--'}</p>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--color-text-strong)' }}>
                              {appt.pet?.name || 'Bilinmiyor'}
                            </p>
                            <p className="text-[11px] truncate" style={{ color: 'var(--color-text-muted)' }}>
                              {appt.pet?.owner?.name} · {TYPE_LABELS[appt.type] || 'Diğer'}
                            </p>
                          </div>
                          <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            appt.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            appt.status === 'cancelled' ? 'bg-rose-100 text-rose-700' :
                            appt.status === 'no-show'   ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {appt.status === 'completed' ? 'Tamamlandı' :
                             appt.status === 'cancelled' ? 'İptal' :
                             appt.status === 'no-show'   ? 'Gelmedi' : 'Planlandı'}
                          </span>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AppointmentCalendar;
